import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import { DateTime } from "luxon";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  MEMBERSHIP_CARD_SHADOW,
  MEMBERSHIP_COLORS,
} from "@/features/pathpick/features/membership/constants";

interface Props {
  // 표시용 플랜명 (BASIC/PRO/...)
  planName: string;
  // 매월 결제일(1~31). null이면 결제 정보 줄을 그리지 않는다.
  renewalDay: number | null;
}

// renewalDay 기준 다음 결제 예정일 — 오늘 이후의 가장 가까운 결제일.
// 오늘이 결제일이면 이번 달이 아니라 다음 달이다(이 카드는 결제 확인된 구독자에게만
// 보이므로, 오늘 결제일이라면 이미 결제된 것으로 본다).
// 말일보다 큰 날(31일 등)은 그 달 말일로 당긴다(백엔드 clamp 규칙과 동일).
const getNextBillingDate = (renewalDay: number) => {
  const today = DateTime.now().startOf("day");
  const clampToMonth = (base: DateTime) =>
    base.set({ day: Math.min(renewalDay, base.daysInMonth ?? 28) });
  const thisMonth = clampToMonth(today);
  return thisMonth > today
    ? thisMonth
    : clampToMonth(today.plus({ months: 1 }).startOf("month"));
};

// 현재 멤버십 관리 카드 — 구독 중(SUBSCRIBED)에만 노출.
// 시안 구조: [타이틀 + "이용 중" 배지] / [플랜명 · 결제주기 · 다음 결제일 한 줄] / 안내문,
// 우측에 해지 문의 버튼. 플랜 변경·해지는 결제가 수동(입금)이라 상담으로 보낸다.
function PathPickMembershipCurrentPlanCard({ planName, renewalDay }: Props) {
  const { t } = useI18nHelper();

  return (
    // 왼쪽 열은 minWidth 0이라 넘치지는 않지만, 해지 문의 버튼이 비축소여서
    // 안내 문구가 ~230px로 눌린다. 모바일에서는 버튼을 아래로 내린다.
    <Flex
      justifyContent={"space-between"}
      flexDirection={{ base: "column", md: "row" }}
      alignItems={{ base: "flex-start", md: "center" }}
      gap={"20px"}
      padding={"18px 20px"}
      border={`1px solid ${MEMBERSHIP_COLORS.cardBorder}`}
      borderRadius={"14px"}
      background={MEMBERSHIP_COLORS.cardBg}
      boxShadow={MEMBERSHIP_CARD_SHADOW}
    >
      <Flex flexDirection={"column"} gap={"8px"} minWidth={0}>
        <Flex gap={"8px"} alignItems={"center"}>
          <Text
            textStyle={"body-md-bold"}
            fontSize={"16px"}
            color={MEMBERSHIP_COLORS.textStrong}
          >
            {t("membership:current_card.title")}
          </Text>
          {/* 시안: 타이틀 옆 파란 필(pill) 배지 */}
          <Flex
            padding={"3px 7px"}
            borderRadius={"full"}
            background={MEMBERSHIP_COLORS.blueBg}
            flexShrink={0}
          >
            <Text textStyle={"body-sm-bold"} color={MEMBERSHIP_COLORS.blueDeep}>
              {t("membership:current_card.badge_active")}
            </Text>
          </Flex>
        </Flex>
        <Flex gap={"6px"} alignItems={"center"} flexWrap={"wrap"}>
          <Text
            textStyle={"body-sm-bold"}
            fontSize={"15px"}
            fontWeight={800}
            color={MEMBERSHIP_COLORS.textStrong}
          >
            {planName}
          </Text>
          {renewalDay != null && (
            <>
              <Text
                textStyle={"body-sm-regular"}
                color={MEMBERSHIP_COLORS.textMuted}
              >
                {t("membership:current_card.billing_day", { day: renewalDay })}
              </Text>
              <Text
                textStyle={"body-sm-regular"}
                color={MEMBERSHIP_COLORS.textMuted}
              >
                {t("membership:current_card.next_billing_date", {
                  date: getNextBillingDate(renewalDay).toFormat("yyyy.MM.dd"),
                })}
              </Text>
            </>
          )}
        </Flex>
        <Text textStyle={"body-sm-regular"} color={MEMBERSHIP_COLORS.textMuted}>
          {t("membership:current_card.note")}
        </Text>
      </Flex>
      <AppButton
        variant={"white"}
        onClick={() => ChannelService.showMessenger()}
        chakraProps={{
          flexShrink: 0,
          height: "40px",
          borderRadius: "10px",
          border: `1px solid ${MEMBERSHIP_COLORS.blueBorder}`,
          background: MEMBERSHIP_COLORS.cardBg,
          padding: "0 18px",
        }}
      >
        <Text
          textStyle={"body-sm-bold"}
          fontWeight={800}
          color={MEMBERSHIP_COLORS.blueDeep}
        >
          {t("membership:current_card.cta_cancel_inquiry")}
        </Text>
      </AppButton>
    </Flex>
  );
}

export default PathPickMembershipCurrentPlanCard;
